/**
 * GranularModule — granular delay built on an AudioWorkletProcessor.
 *
 * The DSP runs in `worklets/granular.worklet.js`. The worklet
 * processor must be loaded on the AudioContext via `engine.loadWorklet()`
 * before this module's `init()` is called, otherwise the
 * AudioWorkletNode constructor throws and the wet path falls back
 * to a pass-through. main.ts handles the load sequencing.
 *
 * Params (all worklet AudioParams except Mix):
 *   - Time     (20ms..2s)   — base delay; grains read this far behind the write head.
 *   - Feedback (0..0.95)    — wet fed back into the buffer; >0.6 starts smearing into texture.
 *   - Size     (20ms..500ms) — duration of each grain. Short = staccato, long = smeary.
 *   - Density  (1..50/sec)  — grains per second. Higher = denser cloud.
 *   - Spread   (0..1)       — jitters grain offset and stereo pan in one knob.
 *   - Pitch    (-24..+24st) — semitones of detune per grain.
 *   - Mix      (0..1)       — equal-power dry/wet crossfade.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

const WORKLET_NAME = 'granular-delay-processor';

export class GranularModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'granular' as const;
  readonly displayName = 'GRAIN';
  readonly id: string;
  bypass = false;

  readonly time = new NumericParameter({
    name: 'Time',
    default: 0.5,
    min: 0.02,
    max: 2.0,
    curve: 'exp',
    unit: 's',
    smoothingMs: 30,
    randomSkew: 1.2,
    formatter: (v) => (v >= 1 ? `${v.toFixed(2)}s` : `${Math.round(v * 1000)}ms`),
  });
  readonly feedback = new NumericParameter({
    name: 'Feedback',
    default: 0.4,
    min: 0,
    max: 0.95,
    curve: 'linear',
    smoothingMs: 30,
    randomSkew: 1.5,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly size = new NumericParameter({
    name: 'Size',
    default: 0.15,
    min: 0.02,
    max: 0.5,
    curve: 'exp',
    unit: 's',
    smoothingMs: 30,
    randomSkew: 1,
    formatter: (v) => `${Math.round(v * 1000)}ms`,
  });
  readonly density = new NumericParameter({
    name: 'Density',
    default: 12,
    min: 1,
    max: 50,
    curve: 'exp',
    smoothingMs: 30,
    randomSkew: 0.8,
    formatter: (v) => `${v.toFixed(0)}/s`,
  });
  readonly spread = new NumericParameter({
    name: 'Spread',
    default: 0.3,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 30,
    randomSkew: 1.5,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });
  readonly pitch = new NumericParameter({
    name: 'Pitch',
    default: 0,
    min: -24,
    max: 24,
    curve: 'linear',
    unit: 'st',
    smoothingMs: 30,
    randomSkew: 1, // uniform — biasing pitch in any direction is unmusical
    formatter: (v) => {
      const r = Math.round(v);
      return r > 0 ? `+${r}` : `${r}`;
    },
  });
  readonly mix = new NumericParameter({
    name: 'Mix',
    default: 0.5,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 20,
    // Bias high — the effect should be audible on a random patch.
    randomSkew: 1.4,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.time,
    this.feedback,
    this.size,
    this.density,
    this.spread,
    this.pitch,
    this.mix,
  ];

  private engine: AudioEngine | null = null;
  private inputNode: GainNode | null = null;
  private outputMixNode: GainNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('grain');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputMixNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.inputNode = ctx.createGain();
    this.outputMixNode = ctx.createGain();
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();

    try {
      // channelCount: 2 forces stereo I/O so the worklet's per-channel
      // grain pan reaches both speakers even when the upstream signal
      // is mono. Without this an upstream mono channel would collapse
      // the worklet's stereo output back to one channel.
      this.worklet = new AudioWorkletNode(ctx, WORKLET_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
    } catch (err) {
      console.warn(
        '[granular] worklet not available — running as pass-through.',
        err,
      );
      this.worklet = null;
    }

    // Dry path
    this.inputNode.connect(this.dryGain);
    this.dryGain.connect(this.outputMixNode);

    // Wet path
    if (this.worklet) {
      this.inputNode.connect(this.worklet);
      this.worklet.connect(this.wetGain);

      // Bind every numeric param to its worklet AudioParam. Mix lives
      // in the wrapper (driving dry/wet gains directly), not in the
      // worklet, so it's not in this list.
      bindWorkletParam(this.worklet, this.time, 'time', ctx);
      bindWorkletParam(this.worklet, this.feedback, 'feedback', ctx);
      bindWorkletParam(this.worklet, this.size, 'size', ctx);
      bindWorkletParam(this.worklet, this.density, 'density', ctx);
      bindWorkletParam(this.worklet, this.spread, 'spread', ctx);
      bindWorkletParam(this.worklet, this.pitch, 'pitch', ctx);
    } else {
      // Pass-through fallback so the FX chain stays intact.
      this.inputNode.connect(this.wetGain);
    }
    this.wetGain.connect(this.outputMixNode);

    this.applyMix(this.mix.value, true);
    this.unsubs.push(this.mix.subscribe((v) => this.applyMix(v)));

    this.started = true;
  }

  /** Mod-routing lookup — every numeric param except Mix is a worklet
   *  AudioParam and can be modulated at audio rate. */
  getAudioParam(paramName: string): AudioParam | null {
    if (!this.worklet) return null;
    const map: Record<string, string> = {
      Time: 'time',
      Feedback: 'feedback',
      Size: 'size',
      Density: 'density',
      Spread: 'spread',
      Pitch: 'pitch',
    };
    const wname = map[paramName];
    if (!wname) return null;
    return this.worklet.parameters.get(wname) ?? null;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.inputNode?.disconnect();
    this.worklet?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.outputMixNode?.disconnect();
    this.inputNode = null;
    this.outputMixNode = null;
    this.worklet = null;
    this.dryGain = null;
    this.wetGain = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  private applyMix(mix: number, instant = false): void {
    if (!this.engine || !this.dryGain || !this.wetGain) return;
    // Equal-power crossfade — same curve as Bitcrusher / Reverb /
    // every other dry+wet effect in the rack so a 50% mix sounds
    // 50% across the whole effects bank.
    const dry = Math.cos((mix * Math.PI) / 2);
    const wet = Math.sin((mix * Math.PI) / 2);
    const t = this.engine.context.currentTime;
    if (instant) {
      this.dryGain.gain.setValueAtTime(dry, t);
      this.wetGain.gain.setValueAtTime(wet, t);
    } else {
      this.dryGain.gain.cancelScheduledValues(t);
      this.wetGain.gain.cancelScheduledValues(t);
      this.dryGain.gain.setTargetAtTime(dry, t, 0.02);
      this.wetGain.gain.setTargetAtTime(wet, t, 0.02);
    }
  }
}

/** Bind a `NumericParameter` to a worklet's named AudioParam if present.
 *  `ctx` is `AudioContext` (not the wider BaseAudioContext) because
 *  Parameter's bindAudioParam needs the resume / state machinery only
 *  the live AudioContext exposes. init() always passes engine.context
 *  which is a real AudioContext, so this is a no-narrow-needed fit. */
function bindWorkletParam(
  node: AudioWorkletNode,
  param: NumericParameter,
  name: string,
  ctx: AudioContext,
): void {
  const ap = node.parameters.get(name);
  if (ap) param.bindAudioParam(ap, ctx);
}
